import {
  ANON_IDENTITY,
  type BlobClient,
  type BlobQueueStore,
  type BlobUploadQueue,
  createBlobUploadQueue,
  indexedDbQueueStore,
  mediaCell,
  memoryQueueStore,
} from '@jbroll/rowboat-client';
import { getBlobClient } from '@/lib/blobs';
import { PHOTO_QUEUE_DB } from '@/lib/storeNames';
import type { Graph } from '@/schema/estate';
import { getIdentifyQueue, type IdentifyQueue } from './identifyQueue';
import { PHOTO_QUEUE_STORE, PHOTO_QUEUED_AT } from './photoQueueDb';
import { attachPhotoKeys } from './photoService';
import { rememberThumb } from './sessionThumbs';
import { makeThumbnail } from './thumbnails';

export type PhotoEntryEvent = 'start' | 'committed';

// jsdom and IDB-less contexts have no IndexedDB: queue in memory there instead of
// throwing on every drain. Real browsers keep the durable store.
export function photoQueueStore(): BlobQueueStore {
  return typeof globalThis.indexedDB === 'undefined'
    ? memoryQueueStore()
    : indexedDbQueueStore(PHOTO_QUEUE_DB, PHOTO_QUEUE_STORE);
}

export interface OwnedQueueStore extends BlobQueueStore {
  /** Takes over adoptable entries (see `ownedQueueStore`); resolves to how many it moved. */
  adopt(): Promise<number>;
  /** When the entry was queued, in ms; undefined for entries queued before times were kept. */
  queuedAt(photoId: string): number | undefined;
}

const OWNER_SEP = ':';

// The times only label entries for the user, so a storage failure costs the label, not the entry.
function readTimes(): Record<string, number> {
  try {
    const times: unknown = JSON.parse(localStorage.getItem(PHOTO_QUEUED_AT) ?? '{}');
    if (typeof times === 'object' && times !== null && !Array.isArray(times))
      return times as Record<string, number>;
    console.warn('photo outbox times are not an object, starting over');
  } catch (err) {
    console.warn('photo outbox times unreadable', err);
  }
  return {};
}

function editTimes(edit: (times: Record<string, number>) => void): void {
  try {
    const times = readTimes();
    edit(times);
    localStorage.setItem(PHOTO_QUEUED_AT, JSON.stringify(times));
  } catch (err) {
    console.warn('photo outbox times not saved', err);
  }
}

/**
 * The outbox as one identity sees it. Entries are keyed `<owner>:<photoId>`, so an account never
 * uploads or counts bytes another account queued in this browser. Entries queued anonymously,
 * and unprefixed ones from before keys carried an owner, follow their photo row: `holds` says the
 * row is in this identity's store, as after the anonymous store is claimed on sign-in.
 */
export function ownedQueueStore(
  base: BlobQueueStore,
  owner: string,
  holds: (photoId: string) => boolean,
): OwnedQueueStore {
  const prefix = `${owner}${OWNER_SEP}`;
  const own = (photoId: string) => prefix + photoId;
  const adopt = async () => {
    let moved = 0;
    for (const key of await base.list()) {
      if (key.startsWith(prefix)) continue;
      const sep = key.indexOf(OWNER_SEP);
      const from = sep < 0 ? null : key.slice(0, sep);
      const photoId = key.slice(sep + 1);
      if ((from !== null && from !== ANON_IDENTITY) || !holds(photoId)) continue;
      const bytes = await base.get(key);
      if (bytes !== undefined) await base.put(own(photoId), bytes);
      await base.delete(key);
      editTimes((times) => {
        if (times[key] !== undefined) times[own(photoId)] = times[key];
        delete times[key];
      });
      moved += 1;
    }
    return moved;
  };
  return {
    put: async (photoId, bytes) => {
      await base.put(own(photoId), bytes);
      editTimes((times) => {
        times[own(photoId)] = Date.now();
      });
    },
    get: async (photoId) => {
      const bytes = await base.get(own(photoId));
      if (bytes !== undefined || !holds(photoId)) return bytes;
      // A preview can read an adoptable entry before a list has moved it.
      return (await base.get(`${ANON_IDENTITY}${OWNER_SEP}${photoId}`)) ?? base.get(photoId);
    },
    delete: async (photoId) => {
      await base.delete(own(photoId));
      editTimes((times) => {
        delete times[own(photoId)];
      });
    },
    list: async () => {
      await adopt();
      return (await base.list())
        .filter((key) => key.startsWith(prefix))
        .map((key) => key.slice(prefix.length));
    },
    adopt,
    queuedAt: (photoId) => readTimes()[own(photoId)],
  };
}

// createBlobUploadQueue reports only totals, but reads each entry with store.get right before
// uploading it (rowboat client/src/blob-queue.ts); a read that finds bytes is the start signal.
function reportingStore(store: BlobQueueStore, onStart: (key: string) => void): BlobQueueStore {
  return {
    ...store,
    get: async (key) => {
      const bytes = await store.get(key);
      if (bytes !== undefined) onStart(key);
      return bytes;
    },
  };
}

export function createPhotoQueue(
  g: Graph,
  opts: {
    /** The photo row's `owner_group_id`, read when its entry drains; a missing row throws. */
    ownerGroupId: (photoId: string) => Promise<string>;
    client?: BlobClient;
    store?: BlobQueueStore;
    makeThumb?: (bytes: Blob) => Promise<Blob>;
    onEntry?: (photoId: string, event: PhotoEntryEvent) => void;
    identifyQueue?: Pick<IdentifyQueue, 'nudge'>;
  },
): BlobUploadQueue {
  const client = opts.client ?? getBlobClient();
  const onEntry = opts.onEntry ?? (() => undefined);
  return createBlobUploadQueue({
    client,
    ownerGroupId: opts.ownerGroupId,
    store: reportingStore(opts.store ?? photoQueueStore(), (key) => onEntry(key, 'start')),
    derivatives: [
      {
        name: 'thumb',
        make: async (b: Blob) => {
          const thumb = await (opts.makeThumb ?? makeThumbnail)(b);
          await rememberThumb(thumb).catch((err) => console.warn('thumbnail not kept', err));
          return thumb;
        },
      },
    ],
    commit: async (photoId, r) => {
      const storage = mediaCell(r.primary);
      const thumb = r.derivatives.thumb;
      // No thumbnail_key when the derivative failed: a tile must never fall back to the original.
      await attachPhotoKeys(g, photoId, { storage, thumbnail: thumb ? mediaCell(thumb) : null });
      onEntry(photoId, 'committed');
      const itemId = g.item_photo(photoId)?.$data.item_id;
      // Not awaited: the nudge waits for the identify drain lock, which a post holds for minutes.
      if (itemId)
        void (opts.identifyQueue ?? getIdentifyQueue())
          .nudge(itemId)
          .catch((err) => console.warn('identify nudge failed', itemId, err));
    },
  });
}
